export const persistedQueryKeys: ReadonlyArray<readonly [string] | readonly [string, string]> = [
  ['ars'],
  ['rmpc'],
  ['rmp'],
  ['cmsSlides'],
  ['address'],
  ['erz'],
  ['dictionary'],
  ['msp', 'dictionary'],
  ['services', 'external'],
  ['services', 'statistics'],
  ['services', 'categories'],
] as const;

export const queryKeys = {
  services: {
    all: ['services'] as const,
    list: () => [...queryKeys.services.all, 'list'] as const,
    external: () => [...queryKeys.services.all, 'external'] as const,
    statistics: () => [...queryKeys.services.all, 'statistics'] as const,
    categories: () => [...queryKeys.services.all, 'categories'] as const,
    weapons: (serviceCode: string) => [...queryKeys.services.all, 'weapons', serviceCode] as const,
    paymentDetails: (requestId: string) => [...queryKeys.services.all, 'paymentDetails', requestId] as const,
    hasActiveRequest: (serviceCode: string) => [...queryKeys.services.all, 'hasActiveRequest', serviceCode] as const,
  },
  faq: {
    all: ['faq'] as const,
    list: () => [...queryKeys.faq.all, 'list'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
  },
  features: {
    all: ['features'] as const,
    list: () => [...queryKeys.features.all, 'list'] as const,
  },
  weapons: {
    all: ['weapons'] as const,
    list: () => [...queryKeys.weapons.all, 'list'] as const,
  },
  permits: {
    all: ['permits'] as const,
    list: () => [...queryKeys.permits.all, 'list'] as const,
  },
  payments: {
    all: ['payments'] as const,
    list: () => [...queryKeys.payments.all, 'list'] as const,
  },
  criminalRecords: {
    all: ['criminalRecords'] as const,
    list: () => [...queryKeys.criminalRecords.all, 'list'] as const,
  },
  insuranceOrders: {
    all: ['insuranceOrders'] as const,
    list: () => [...queryKeys.insuranceOrders.all, 'list'] as const,
  },
  bia: {
    products: (requestId: string) => ['bia', 'products', requestId] as const,
  },
  search: {
    wantedPersonSearchList: () => ['search', 'wantedPersonSearch', 'list'] as const,
    missingPersonSearchList: () => ['search', 'missingPersonSearch', 'list'] as const,
    missingChildrenSearchList: () => ['search', 'missingChildrenSearch', 'list'] as const,
  },
  userRequests: {
    all: ['userRequests'] as const,
    filters: (scope: string) => [...queryKeys.userRequests.all, 'filters', scope] as const,
    list: (scope: string, page: number, filters: object) =>
      [...queryKeys.userRequests.all, 'list', scope, page, filters] as const,
    drafts: () => [...queryKeys.userRequests.all, 'drafts'] as const,
    draft: (serviceCode: string) => [...queryKeys.userRequests.all, 'draft', serviceCode] as const,
  },
  rmpc: {
    tattooCategories: () => ['rmpc', 'tattooCategories'] as const,
    tattooBodyLocations: () => ['rmpc', 'tattooBodyLocations'] as const,
    tattooSearch: (page: number, filters?: object | null) => ['rmpc', 'tattooSearch', page, filters ?? null] as const,
  },
  ars: {
    authorTypes: () => ['ars', 'authorTypes'] as const,
    appealKinds: () => ['ars', 'appealKinds'] as const,
  },
  auth: {
    profile: () => ['auth', 'profile'] as const,
    storedConfig: () => ['auth', 'storedConfig'] as const,
  },
  erz: {
    all: ['erz'] as const,
    documentTypes: () => [...queryKeys.erz.all, 'documentTypes'] as const,
    weaponDocuments: () => [...queryKeys.erz.all, 'weaponDocuments'] as const,
    reapplicationOrders: () => [...queryKeys.erz.all, 'reapplicationOrders'] as const,
  },
  dictionary: {
    all: ['dictionary'] as const,
    countries: () => [...queryKeys.dictionary.all, 'countries'] as const,
    authorities: () => [...queryKeys.dictionary.all, 'authorities'] as const,
  },
  rmp: {
    all: ['rmp'] as const,
    militaryServiceTypes: () => [...queryKeys.rmp.all, 'militaryServiceTypes'] as const,
    statureTypes: () => [...queryKeys.rmp.all, 'statureTypes'] as const,
    hairColors: () => [...queryKeys.rmp.all, 'hairColors'] as const,
    eyeColors: () => [...queryKeys.rmp.all, 'eyeColors'] as const,
    bloodGroups: () => [...queryKeys.rmp.all, 'bloodGroups'] as const,
    familyRelationshipDegrees: (serviceCode: string) =>
      [...queryKeys.rmp.all, 'familyRelationshipDegrees', serviceCode] as const,
    personStatuses: () => [...queryKeys.rmp.all, 'personStatuses'] as const,
  },
  address: {
    all: ['address'] as const,
    countries: () => [...queryKeys.address.all, 'countries'] as const,
    regions: () => [...queryKeys.address.all, 'regions'] as const,
    districts: (regionId: string) => [...queryKeys.address.all, 'districts', regionId] as const,
    settlements: (districtId: string) => [...queryKeys.address.all, 'settlements', districtId] as const,
    streets: (settlementId: string) => [...queryKeys.address.all, 'streets', settlementId] as const,
  },
  msp: {
    all: ['msp'] as const,
    dictionary: {
      all: ['msp', 'dictionary'] as const,
      sportRegions: (sportId = '') => [...queryKeys.msp.dictionary.all, 'sportRegions', sportId] as const,
      sports: (regionId: string) => [...queryKeys.msp.dictionary.all, 'sports', regionId] as const,
      sportCenters: (regionId: string, sportId?: string) =>
        [...queryKeys.msp.dictionary.all, 'sportCenters', regionId, sportId] as const,
      needs: () => [...queryKeys.msp.dictionary.all, 'needs'] as const,
      regions: () => [...queryKeys.msp.dictionary.all, 'regions'] as const,
      districts: (regionId: string) => [...queryKeys.msp.dictionary.all, 'districts', regionId] as const,
      cities: (districtId: string) => [...queryKeys.msp.dictionary.all, 'cities', districtId] as const,
      educationTypes: () => [...queryKeys.msp.dictionary.all, 'educationTypes'] as const,
      driverLicenseCategories: () => [...queryKeys.msp.dictionary.all, 'driverLicenseCategories'] as const,
      dmsSubdivisions: () => [...queryKeys.msp.dictionary.all, 'dmsSubdivisions'] as const,
      serviceCenters: (regionId?: string) => [...queryKeys.msp.dictionary.all, 'serviceCenters', regionId] as const,
      employmentExperienceAreas: () => [...queryKeys.msp.dictionary.all, 'employmentExperienceAreas'] as const,
      employmentDepartments: (regionId?: string) =>
        [...queryKeys.msp.dictionary.all, 'employmentDepartments', regionId] as const,
      employmentDepartmentDirections: (departmentId?: string) =>
        [...queryKeys.msp.dictionary.all, 'employmentDepartmentDirections', departmentId] as const,
    },
    facility: {
      all: ['msp', 'facility'] as const,
      list: (categoryId: string, filters: Record<string, unknown>) =>
        [...queryKeys.msp.facility.all, 'list', categoryId, filters] as const,
    },
    socialProtect: {
      all: ['msp', 'socialProtect'] as const,
      sportCenter: (sportCenterId: string) =>
        [...queryKeys.msp.socialProtect.all, 'sportCenter', sportCenterId] as const,
      personalData: () => [...queryKeys.msp.socialProtect.all, 'personalData'] as const,
      appeals: () => [...queryKeys.msp.socialProtect.all, 'appeals'] as const,
      appealDetails: (appealId: string) => [...queryKeys.msp.socialProtect.all, 'appealDetails', appealId] as const,
      mentors: () => [...queryKeys.msp.socialProtect.all, 'mentors'] as const,
      treatments: () => [...queryKeys.msp.socialProtect.all, 'treatments'] as const,
      services: () => [...queryKeys.msp.socialProtect.all, 'services'] as const,
      incidents: () => [...queryKeys.msp.socialProtect.all, 'incidents'] as const,
      contacts: () => [...queryKeys.msp.socialProtect.all, 'contacts'] as const,
      supports: () => [...queryKeys.msp.socialProtect.all, 'supports'] as const,
      needs: () => [...queryKeys.msp.socialProtect.all, 'needs'] as const,
      orderAvailability: () => [...queryKeys.msp.socialProtect.all, 'orderAvailability'] as const,
      invalidity: () => [...queryKeys.msp.socialProtect.all, 'invalidity'] as const,
      news: (page: number, pageSize: number) => [...queryKeys.msp.socialProtect.all, 'news', page, pageSize] as const,
      newsItem: (newsId: string) => [...queryKeys.msp.socialProtect.all, 'newsItem', newsId] as const,
    },
    mapsAuth: () => [...queryKeys.msp.all, 'mapsAuth'] as const,
  },
} as const;
